import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { BookText, Gavel, FileText, ShieldCheck } from "lucide-react";

type SegId = "s1" | "s2" | "s3" | "s4";
type SrcId = "a1" | "a2" | "a3";

const SEGMENTS: SegId[] = ["s1", "s2", "s3", "s4"];
const SOURCES: SrcId[] = ["a1", "a2", "a3"];

/** Which source each answer phrase is anchored to. */
const LINKS: Record<SegId, SrcId> = {
  s1: "a1",
  s2: "a2",
  s3: "a2",
  s4: "a3",
};

const SOURCE_ICON: Record<SrcId, typeof BookText> = {
  a1: BookText,
  a2: Gavel,
  a3: FileText,
};

type Point = { x: number; y: number };

/**
 * Signature hero: a real legal answer whose key phrases are connected by thin
 * SVG lines to the sources that justify them (code articles, case law, the
 * uploaded document). Hovering a phrase lights its source and vice-versa.
 * Lines are recomputed from real layout positions on resize / font load and
 * are aria-hidden — the answer itself is plain readable text for screen readers.
 */
export function SourceTracedHero() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();

  const containerRef = useRef<HTMLDivElement>(null);
  const segRefs = useRef<Record<string, HTMLElement | null>>({});
  const srcRefs = useRef<Record<string, HTMLElement | null>>({});

  const [paths, setPaths] = useState<Record<SegId, { d: string; from: Point; to: Point }>>(
    {} as Record<SegId, { d: string; from: Point; to: Point }>,
  );
  const [active, setActive] = useState<SrcId | null>(null);
  const [hoverSeg, setHoverSeg] = useState<SegId | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const rafRef = useRef<number | null>(null);

  const compute = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const box = container.getBoundingClientRect();
    const next = {} as Record<SegId, { d: string; from: Point; to: Point }>;
    for (const seg of SEGMENTS) {
      const segEl = segRefs.current[seg];
      const srcEl = srcRefs.current[LINKS[seg]];
      if (!segEl || !srcEl) continue;
      const s = segEl.getBoundingClientRect();
      const d = srcEl.getBoundingClientRect();
      const from: Point = {
        x: s.right - box.left,
        y: s.top + s.height / 2 - box.top,
      };
      const to: Point = {
        x: d.left - box.left,
        y: d.top + d.height / 2 - box.top,
      };
      const midX = from.x + (to.x - from.x) * 0.5;
      const path = `M ${from.x} ${from.y} C ${midX} ${from.y}, ${midX} ${to.y}, ${to.x} ${to.y}`;
      next[seg] = { d: path, from, to };
    }
    setSize({ w: box.width, h: box.height });
    setPaths(next);
  }, []);

  /** Coalesce bursts of resize/observer callbacks into one rAF-scheduled layout read. */
  const scheduleCompute = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      compute();
    });
  }, [compute]);

  useLayoutEffect(() => {
    compute();
  }, [compute, t]);

  useEffect(() => {
    const onResize = () => scheduleCompute();
    window.addEventListener("resize", onResize, { passive: true });
    const ro = new ResizeObserver(() => scheduleCompute());
    if (containerRef.current) ro.observe(containerRef.current);
    // recompute once fonts settle
    const id = window.setTimeout(scheduleCompute, 300);
    if ((document as any).fonts?.ready) {
      (document as any).fonts.ready.then(() => scheduleCompute());
    }
    return () => {
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      window.clearTimeout(id);
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleCompute]);


  return (
    <div
      ref={containerRef}
      className="relative grid grid-cols-1 gap-6 lg:grid-cols-[1.35fr_0.9fr] lg:gap-10"
    >
      {/* Connector overlay (desktop only) */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 z-20 hidden lg:block"
        width={size.w}
        height={size.h}
        viewBox={`0 0 ${size.w} ${size.h}`}
        fill="none"
      >
        {SEGMENTS.map((seg, i) => {
          const p = paths[seg];
          if (!p) return null;
          const lit =
            active === LINKS[seg] || hoverSeg === seg;
          return (
            <g key={seg}>
              <motion.path
                d={p.d}
                stroke="hsl(var(--accent))"
                strokeWidth={lit ? 1.6 : 1}
                strokeOpacity={lit ? 0.95 : 0.28}
                initial={reduce ? false : { pathLength: 0, opacity: 0 }}
                animate={
                  reduce
                    ? { pathLength: 1, opacity: 1 }
                    : { pathLength: 1, opacity: 1 }
                }
                transition={{ duration: 0.9, delay: 0.5 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                style={{ filter: lit ? "drop-shadow(0 0 6px hsl(var(--accent) / 0.6))" : "none" }}
              />
              <circle cx={p.from.x} cy={p.from.y} r={lit ? 3 : 2} fill="hsl(var(--accent))" opacity={lit ? 1 : 0.45} />
              <circle cx={p.to.x} cy={p.to.y} r={lit ? 3 : 2} fill="hsl(var(--accent))" opacity={lit ? 1 : 0.45} />
            </g>
          );
        })}
      </svg>

      {/* Answer panel */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="glass-panel relative z-10 p-6 sm:p-7"
      >
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-accent">
          <ShieldCheck className="h-3.5 w-3.5" />
          {t("heroTrace.verified")}
        </div>

        <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">
            {t("heroTrace.questionLabel")}
          </div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-white/90">
            {t("heroTrace.question")}
          </p>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/55">
            <span className="flex h-4 w-4 items-center justify-center rounded bg-accent text-[8px] font-bold text-accent-foreground">
              L
            </span>
            {t("heroTrace.responseLabel")}
          </div>
          <p className="mt-2 text-[15px] leading-[1.85] text-white/85">
            {SEGMENTS.map((seg, i) => {
              const lit = active === LINKS[seg] || hoverSeg === seg;
              return (
                <span key={seg}>
                  <motion.span
                    ref={(el) => {
                      segRefs.current[seg] = el;
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={lit}
                    aria-label={`${t(`heroTrace.segments.${seg}`)} — ${t(`heroTrace.sources.${LINKS[seg]}.ref`)}`}
                    onMouseEnter={() => {
                      setHoverSeg(seg);
                      setActive(LINKS[seg]);
                    }}
                    onMouseLeave={() => {
                      setHoverSeg(null);
                      setActive(null);
                    }}
                    onFocus={() => {
                      setHoverSeg(seg);
                      setActive(LINKS[seg]);
                    }}
                    onBlur={() => {
                      setHoverSeg(null);
                      setActive(null);
                    }}
                    onClick={() => {
                      // touch / pointer toggle
                      const next = hoverSeg === seg ? null : seg;
                      setHoverSeg(next);
                      setActive(next ? LINKS[seg] : null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const next = hoverSeg === seg ? null : seg;
                        setHoverSeg(next);
                        setActive(next ? LINKS[seg] : null);
                      }
                    }}
                    initial={reduce ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.25 }}
                    className={`cursor-pointer rounded px-0.5 outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-accent ${
                      lit
                        ? "bg-accent/15 text-white [box-shadow:inset_0_-1px_0_hsl(var(--accent))]"
                        : "text-white/85"
                    }`}
                  >
                    {t(`heroTrace.segments.${seg}`)}
                  </motion.span>
                  {i < SEGMENTS.length - 1 ? ". " : "."}
                </span>
              );
            })}
          </p>
          <div className="mt-4 text-[11px] text-white/40">{t("heroTrace.hint")}</div>
        </div>
      </motion.div>

      {/* Sources column */}
      <motion.div
        initial={reduce ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 flex flex-col gap-3"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">
          {t("heroTrace.sourcesLabel")}
        </div>
        {SOURCES.map((src, i) => {
          const Icon = SOURCE_ICON[src];
          const lit =
            active === src || (hoverSeg !== null && LINKS[hoverSeg] === src);
          return (
            <motion.div
              key={src}
              ref={(el) => {
                srcRefs.current[src] = el;
              }}
              role="button"
              tabIndex={0}
              aria-pressed={lit}
              aria-label={`${t(`heroTrace.sources.${src}.ref`)} — ${t(`heroTrace.sources.${src}.note`)}`}
              onMouseEnter={() => setActive(src)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(src)}
              onBlur={() => setActive(null)}
              onClick={() => setActive((prev) => (prev === src ? null : src))}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActive((prev) => (prev === src ? null : src));
                }
              }}
              initial={reduce ? false : { opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
              className={`cursor-pointer rounded-xl border p-3.5 outline-none transition-all duration-200 focus-visible:ring-2 focus-visible:ring-accent ${
                lit
                  ? "border-accent/60 bg-accent/[0.08] [box-shadow:0_0_0_1px_hsl(var(--accent)/0.4),0_12px_30px_-12px_hsl(var(--accent)/0.5)]"
                  : "border-white/10 bg-white/[0.03]"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
                    lit ? "bg-accent text-accent-foreground" : "bg-white/[0.06] text-white/70"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="font-mono text-[13px] font-medium text-white">
                  {t(`heroTrace.sources.${src}.ref`)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-white/55">{t(`heroTrace.sources.${src}.note`)}</span>
                <span className="shrink-0 rounded-full border border-white/10 px-2 py-0.5 text-[9px] uppercase tracking-wider text-white/45">
                  {t(`heroTrace.sources.${src}.kind`)}
                </span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
